// src/features/tenants/components/TenantCreateModal.tsx
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { Modal } from '../../../app/components/foundation';
import { useCreateTenant } from '../hooks/useCreateTenant';

export interface TenantCreateModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create — page decides refetch/close. */
  onCreated: () => void;
}

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * "افزودن تننت" flow. Owns its own form state (name/slug) and talks
 * to the real create-tenant endpoint through useCreateTenant — no
 * mock data involved, and tenants.mock.ts is not touched anywhere in
 * this component.
 *
 * Built on the shared Modal primitive (new — there was no Modal/
 * Dialog in foundation before this feature) plus plain inputs styled
 * via tenants.css, same convention as the rest of this feature: no
 * new UI library, no new dependency.
 *
 * TenantsPage owns `open`/`onClose` and refetching; this component
 * never reads or mutates the tenants list itself, it only reports
 * success via `onCreated`.
 */
export function TenantCreateModal({ open, onClose, onCreated }: TenantCreateModalProps) {
  const { submit, submitting, error, resetError } = useCreateTenant();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [fullName, setFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  function resetForm() {
    setName('');
    setSlug('');
    setFullName('');
    setMobile('');
    setPassword('');
    setShowPassword(false);
    setSlugTouched(false);
    setValidationError(null);
    resetError();
  }

  function handleClose() {
    if (submitting) return;
    resetForm();
    onClose();
  }

  function handleNameChange(value: string) {
    setName(value);
    if (!slugTouched) {
      setSlug(slugify(value));
    }
  }

  function handleSlugChange(value: string) {
    setSlugTouched(true);
    setSlug(value);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setValidationError(null);

    const trimmedName = name.trim();
    const normalizedSlug = slug.trim().toLowerCase();

    if (!trimmedName) {
      setValidationError('نام تننت الزامی است.');
      return;
    }

    if (!normalizedSlug || !SLUG_PATTERN.test(normalizedSlug)) {
      setValidationError('دامنه باید فقط شامل حروف انگلیسی، عدد و خط تیره باشد.');
      return;
    }

    if (!fullName.trim()) {
      setValidationError('نام نماینده الزامی است.');
      return;
    }

    if (!/^09\d{9}$/.test(mobile.trim())) {
      setValidationError('شماره موبایل باید با 09 شروع شده و ۱۱ رقم باشد.');
      return;
    }

    if (password.length < 8) {
      setValidationError('رمز عبور نماینده باید حداقل ۸ کاراکتر باشد.');
      return;
    }

    try {
      await submit({
        name: trimmedName,
        slug: normalizedSlug,
        representative: {
          full_name: fullName.trim(),
          mobile: mobile.trim(),
          password,
        },
      });
      resetForm();
      onCreated();
    } catch {
      // submit() already set the hook's error state.
    }
  }

  return (
    <Modal open={open} onClose={handleClose} title="افزودن تننت">
      <form className="vania-tenant-create-form" onSubmit={handleSubmit}>
        <label className="vania-tenant-create-field">
          <span>نام تننت</span>
          <input
            type="text"
            value={name}
            onChange={(event) => handleNameChange(event.target.value)}
            placeholder="مثلاً نمایندگی تهران"
            disabled={submitting}
            autoFocus
          />
        </label>

        <label className="vania-tenant-create-field">
          <span>نام نماینده اولیه</span>
          <input
            type="text"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            placeholder="مثلاً علی رضایی"
            disabled={submitting}
          />
        </label>

        <label className="vania-tenant-create-field">
          <span>موبایل نماینده اولیه</span>
          <input
            type="tel"
            dir="ltr"
            value={mobile}
            onChange={(event) => setMobile(event.target.value)}
            placeholder="09120000000"
            disabled={submitting}
            autoComplete="tel"
          />
        </label>

        <label className="vania-tenant-create-field">
          <span>رمز عبور نماینده اولیه</span>
          <div className="vania-tenant-password-field">
            <input
              type={showPassword ? 'text' : 'password'}
              dir="ltr"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="حداقل ۸ کاراکتر"
              disabled={submitting}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="vania-tenant-password-toggle"
              onClick={() => setShowPassword((visible) => !visible)}
              aria-label={showPassword ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
              title={showPassword ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
              disabled={submitting}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        <label className="vania-tenant-create-field">
          <span>دامنه (slug)</span>
          <input
            type="text"
            dir="ltr"
            value={slug}
            onChange={(event) => handleSlugChange(event.target.value)}
            placeholder="tehran"
            disabled={submitting}
          />
        </label>

        {(validationError || error) && (
          <p className="vania-tenant-create-error">{validationError ?? error}</p>
        )}

        <div className="vania-tenant-create-actions">
          <button
            type="button"
            className="vania-tenant-create-cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            انصراف
          </button>
          <button type="submit" className="vania-tenant-create-submit" disabled={submitting}>
            {submitting ? 'در حال ایجاد...' : 'ایجاد تننت'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
